import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Bus, Truck, Settings2 } from "lucide-react";
import { VEHICULOS } from "@/lib/constants";

const cupoDefault = (tipo, tour, modalidad) => {
  const m = (tour?.modalidades || []).find((mm) => mm.vehiculo === tipo && mm.tipo === modalidad);
  if (m) return m.cupo_max;
  return tipo === "bus" ? 54 : 13;
};

export default function SalidaConfigDialog({ open, onOpenChange, salida, tour, onSaved }) {
  const [vehiculos, setVehiculos] = useState([]);
  const [overrideOn, setOverrideOn] = useState(false);
  const [overrideVal, setOverrideVal] = useState("");
  const [nota, setNota] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && salida) {
      setVehiculos(salida.vehiculos && salida.vehiculos.length ? salida.vehiculos : []);
      setOverrideOn(salida.cupo_override != null);
      setOverrideVal(salida.cupo_override != null ? String(salida.cupo_override) : "");
      setNota(salida.nota || "");
    }
  }, [open, salida]);

  const agregarVehiculo = (tipo) => {
    setVehiculos((prev) => [...prev, { tipo, cupo: cupoDefault(tipo, tour, salida?.modalidad) }]);
  };

  const actualizarCupo = (idx, val) => {
    setVehiculos((prev) => prev.map((v, i) => (i === idx ? { ...v, cupo: parseInt(val, 10) || 0 } : v)));
  };

  const quitarVehiculo = (idx) => {
    setVehiculos((prev) => prev.filter((_, i) => i !== idx));
  };

  const totalVehiculos = vehiculos.reduce((acc, v) => acc + (parseInt(v.cupo, 10) || 0), 0);
  const cupoFinal = overrideOn ? parseInt(overrideVal, 10) || 0 : totalVehiculos;

  const guardar = async () => {
    setSaving(true);
    try {
      await api.put(`/salidas/${salida.id}/config`, {
        vehiculos: vehiculos.map((v) => ({ tipo: v.tipo, cupo: parseInt(v.cupo, 10) || 0 })),
        cupo_override: overrideOn ? parseInt(overrideVal, 10) || 0 : null,
        nota,
      });
      toast.success(`Salida configurada. Cupo total: ${cupoFinal}.`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="salida-config-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Settings2 className="h-5 w-5" /> Configurar vehículos y cupo
          </DialogTitle>
          <DialogDescription>
            Definí qué vehículos salen este día para esta salida. El cupo total se suma automáticamente
            (podés sobrescribirlo a mano).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Vehículos del día</Label>
            {vehiculos.length === 0 ? (
              <p className="text-xs text-slate-400 py-2">
                Sin vehículos cargados — usando el cupo de catálogo ({salida?.cupo_max}). Agregá uno o más
                abajo para definir la flota real de hoy.
              </p>
            ) : (
              <div className="space-y-2">
                {vehiculos.map((v, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`vehiculo-row-${i}`}>
                    <span className="inline-flex items-center gap-1.5 text-sm text-slate-700 w-16">
                      {v.tipo === "van" ? <Truck className="h-4 w-4" /> : <Bus className="h-4 w-4" />}
                      {VEHICULOS[v.tipo]}
                    </span>
                    <Input
                      type="number"
                      min={0}
                      value={v.cupo}
                      onChange={(e) => actualizarCupo(i, e.target.value)}
                      className="h-9 w-24"
                      data-testid={`vehiculo-cupo-${i}`}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-slate-400 hover:text-red-600"
                      onClick={() => quitarVehiculo(i)}
                      data-testid={`vehiculo-quitar-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => agregarVehiculo("bus")}
                data-testid="agregar-bus-btn"
              >
                <Plus className="h-3.5 w-3.5" /> Bus
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => agregarVehiculo("van")}
                data-testid="agregar-van-btn"
              >
                <Plus className="h-3.5 w-3.5" /> Van
              </Button>
            </div>
          </div>

          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex items-center justify-between">
            <span className="text-xs text-slate-500">Cupo total calculado</span>
            <span className="text-base font-bold tabular-nums text-slate-900" data-testid="cupo-total-calculado">
              {totalVehiculos || "—"}
            </span>
          </div>

          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={overrideOn}
                onCheckedChange={(v) => setOverrideOn(!!v)}
                data-testid="cupo-override-checkbox"
              />
              Sobrescribir el cupo total manualmente
            </label>
            {overrideOn && (
              <Input
                type="number"
                min={0}
                value={overrideVal}
                onChange={(e) => setOverrideVal(e.target.value)}
                placeholder="Cupo total"
                data-testid="cupo-override-input"
              />
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Nota para el equipo (opcional)</Label>
            <Textarea
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder='Ej: "1 bus + 1 van" o cualquier aclaración para esta salida'
              rows={2}
              data-testid="salida-nota-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={guardar} disabled={saving} data-testid="salida-config-guardar">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : `Guardar (cupo ${cupoFinal || 0})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
